import React, { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { TrashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import GoalsModel, { goalTypes } from "../models/GoalsModel";
import Heatmap from "./Heatmap";
import eventBus from "../eventBus";

const Goal = ({ goal }) => {
  const [days, setDays] = useState(goal.days);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const toHaveDisplayValueDate = new Date();
  const toHaveDisplayComponents = {
    day: toHaveDisplayValueDate.getDate(),
    month: toHaveDisplayValueDate.getMonth() + 1,
    year: toHaveDisplayValueDate.getFullYear(),
  };

  const didToday = days.find((d) => {
    const date = new Date(d);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return (
      day === toHaveDisplayComponents.day &&
      month === toHaveDisplayComponents.month &&
      year === toHaveDisplayComponents.year
    );
  });

  const handleClicked = () => {
    const date = new Date();
    const dayDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const dayDateMillis = dayDate.getTime();

    const newCheckedDays = new Set(days);
    if (didToday) {
      newCheckedDays.delete(dayDateMillis);
    } else {
      newCheckedDays.add(dayDateMillis);
    }
    setDays(Array.from(newCheckedDays));
    GoalsModel.update(goal, { days: Array.from(newCheckedDays) });
    eventBus.dispatch('refreshGoals')
  };

  const emoji = goalTypes[goal.type]?.emoji;

  function formatDuration(minutes) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const hoursDisplay = hours > 0 ? `${hours} hr${hours > 1 ? "s" : ""}` : "";
    const minutesDisplay =
      remainingMinutes > 0
        ? `${remainingMinutes} min${remainingMinutes > 1 ? "s" : ""}`
        : "";
    return `${hoursDisplay} ${minutesDisplay}`.trim();
  }

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  const handleDelete = () => {
    GoalsModel.delete(goal.id); // Assuming GoalsModel has a delete method
    closeDialog();
    eventBus.dispatch("refreshGoals")
  };

  return (
    <div className="h-[100%]">
      <div className="flex items-center justify-between">
        <div className="cursor-pointer flex align-center" onClick={openDialog}>
          {emoji}{" "}<div className=" font-bold ml-1">{goal.goal}</div>
        </div>

        <div className="flex h-6 items-center">
          <input
            id="comments"
            checked={didToday}
            aria-describedby="comments-description"
            name="comments"
            type="checkbox"
            onChange={() => handleClicked()}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
          />
          <div className="ml-2">
            {didToday ? (
              <span className="inline-flex items-center rounded-md bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                DONE
              </span>
            ) : (
              <span className="inline-flex items-center rounded-md bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
                TODO
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-8">
        <Heatmap days={days} />

        {goal.duration > 0 && (
          <span className="inline-flex items-center rounded-md bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 whitespace-nowrap">
            <p>{formatDuration(goal.duration)}</p>
          </span>
        )}
      </div>

      {/* Dialog for goal deletion */}
      <Transition.Root show={isDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeDialog}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                    <button
                      type="button"
                      className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      onClick={closeDialog}
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900"
                      >
                        Are you sure you want to delete this goal?
                      </Dialog.Title>
                      <div className="mt-4">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                          onClick={handleDelete}
                        >
                          <TrashIcon
                            className="w-5 h-5 mr-2"
                            aria-hidden="true"
                          />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default Goal;
